import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import ItemForm from './ItemForm';

const API_BASE_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:4000'
  : '';

const ellipsisCell = {
  maxWidth: 220,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

const ItemList = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/items`);
      if (!response.ok) {
        throw new Error('Failed to fetch items');
      }
      const data = await response.json();
      setItems(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateItem = async (itemData) => {
    try {
      const formData = new FormData();
      formData.append('name', itemData.name);
      formData.append('cost', itemData.cost);
      formData.append('type', itemData.type);
      formData.append('description', itemData.description);
      formData.append('instruction', itemData.instruction);

      if (itemData.iconFile) {
        formData.append('icon', itemData.iconFile);
      }

      const response = await fetch(`${API_BASE_URL}/api/items`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to create item');
      }

      await fetchItems();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateItem = async (itemData) => {
    try {
      const formData = new FormData();
      formData.append('name', itemData.name);
      formData.append('cost', itemData.cost);
      formData.append('type', itemData.type);
      formData.append('description', itemData.description);
      formData.append('instruction', itemData.instruction);

      if (itemData.iconFile) {
        formData.append('icon', itemData.iconFile);
      } else if (itemData.existingIcon) {
        formData.append('existingIcon', itemData.existingIcon);
      }

      const response = await fetch(`${API_BASE_URL}/api/items/${editingItem._id}`, {
        method: 'PUT',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to update item');
      }

      await fetchItems();
      setEditingItem(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteItem = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/items/${itemToDelete._id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete item');
      }

      await fetchItems();
      setItemToDelete(null);
      setDeleteDialogOpen(false);
    } catch (err) {
      setError(err.message);
    }
  };

  const openEditForm = (item) => {
    setEditingItem(item);
    setFormOpen(true);
  };

  const openDeleteDialog = (item) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingItem(null);
  };

  if (loading) {
    return <Typography>Loading items...</Typography>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormOpen(true)}>
          Add Item
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell width={56} />
              <TableCell>Name</TableCell>
              <TableCell width={88}>Cost</TableCell>
              <TableCell width={100}>Type</TableCell>
              <TableCell>Instruction</TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap', minWidth: 112 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No items
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={item._id} hover>
                  <TableCell>
                    <Avatar
                      variant="rounded"
                      src={item.icon || undefined}
                      alt=""
                      sx={{ width: 40, height: 40, bgcolor: 'action.hover', fontSize: '0.75rem' }}
                    >
                      {!item.icon ? (item.name || '?').charAt(0).toUpperCase() : null}
                    </Avatar>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{item.name}</TableCell>
                  <TableCell>{item.cost}</TableCell>
                  <TableCell>
                    <Chip label={item.type || '—'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell
                    title={item.instruction || ''}
                    sx={{
                      ...ellipsisCell,
                      maxWidth: 280,
                      ...(item.instruction
                        ? {
                            bgcolor: 'rgba(255,255,255,0.05)',
                            borderLeft: '3px solid',
                            borderColor: 'divider'
                          }
                        : {})
                    }}
                  >
                    {item.instruction || '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', minWidth: 112 }}>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        flexDirection: 'row',
                        flexWrap: 'nowrap',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 0.25
                      }}
                    >
                      <IconButton size="small" onClick={() => openEditForm(item)} color="primary">
                        <Edit />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => openDeleteDialog(item)}>
                        <Delete />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ItemForm
        open={formOpen}
        onClose={closeForm}
        onSubmit={editingItem ? handleUpdateItem : handleCreateItem}
        item={editingItem}
        isEdit={!!editingItem}
      />

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Item</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete &quot;{itemToDelete?.name}&quot;?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteItem} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ItemList;
